import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { organizeService } from "../services/organizeService";
import { AWAITING_REVIEW_KEY } from "./useOrganizeAwaitingReview";
import type { FolderOrganizeRequest, OrganizeDimension, OrganizeScope } from "../types/organize";
import { ACTIVE_ORGANIZE_STATUSES } from "../types/organize";

interface UseFolderOrganizeOptions {
  buildingId: string;
  projectId: string;
  /** Folder to organise; null = the PROJECT ROOT (loose files with no folder). */
  folderId: number | null;
  /**
   * Gate for fetching/watching (default true). The dialog passes its `open`
   * flag so a closed-but-mounted dialog doesn't keep polling and holding a
   * realtime channel for a stale folder; the toolbar watcher stays always-on.
   */
  enabled?: boolean;
  /** Called after a proposal is applied so the view can refresh its documents. */
  onApplied?: () => void;
}

export const requestKey = (projectId: number | string, folderId: number | null) => [
  "folder-organize",
  String(projectId),
  folderId ?? "root",
];

function isActive(request: FolderOrganizeRequest | null | undefined): boolean {
  return !!request && (ACTIVE_ORGANIZE_STATUSES as readonly string[]).includes(request.status);
}

// The toolbar watcher and the dialog both mount this hook for the same
// folder, so refcount the realtime channel per request row — one channel,
// one cache write per event, no matter how many hook instances watch it.
const liveChannels = new Map<number, { channel: RealtimeChannel; refs: number }>();

function watchRequestRow(requestId: number, queryClient: QueryClient): () => void {
  let entry = liveChannels.get(requestId);
  if (!entry) {
    const channel = supabase
      .channel(`folder_organize_${requestId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "folder_organize_requests",
          filter: `id=eq.${requestId}`,
        },
        (payload) => {
          const row = payload.new as FolderOrganizeRequest;
          queryClient.setQueryData(requestKey(row.project_id, row.folder_id), row);
        }
      )
      .subscribe();
    entry = { channel, refs: 0 };
    liveChannels.set(requestId, entry);
  }
  entry.refs += 1;
  return () => {
    entry.refs -= 1;
    if (entry.refs === 0) {
      liveChannels.delete(requestId);
      supabase.removeChannel(entry.channel);
    }
  };
}

/**
 * Lifecycle of one folder's AI-organize run (WHA-3654).
 *
 * The request row is the single source of truth; this hook keeps a
 * react-query cache entry for the folder's active (or just-finished)
 * request, updated by a Supabase realtime subscription with a slow
 * polling fallback while a run is in flight.
 */
export function useFolderOrganize({
  buildingId,
  projectId,
  folderId,
  enabled = true,
  onApplied,
}: UseFolderOrganizeOptions) {
  const queryClient = useQueryClient();
  const numericProjectId = Number(projectId);
  // Runs are per-user: only the requester resumes into their own proposal, so
  // the fetch waits for the profile rather than reading the folder's rows
  // unscoped. The cache key stays folder-scoped — a session has one user, and
  // OrganizeWatcher writes into it from the same user's realtime rows.
  const profileId = useAuth().userProfile?.id ?? null;

  const requestQuery = useQuery({
    queryKey: requestKey(projectId, folderId),
    queryFn: () =>
      Number.isNaN(numericProjectId) || profileId === null
        ? Promise.resolve(null)
        : organizeService.getActiveRequestForFolder(folderId, numericProjectId, profileId),
    enabled: enabled && !Number.isNaN(numericProjectId) && profileId !== null,
    // Realtime is the primary signal; poll as a safety net while in flight.
    refetchInterval: (query) => (isActive(query.state.data) ? 10_000 : false),
  });

  const request = requestQuery.data ?? null;
  // Gate the subscription too — a disabled instance can still see cached
  // active-request data under the shared query key.
  const activeRequestId = enabled && request && isActive(request) ? request.id : null;

  // Realtime: watch the active request row for status/proposal updates.
  useEffect(() => {
    if (activeRequestId === null) return;
    return watchRequestRow(activeRequestId, queryClient);
  }, [activeRequestId, queryClient]);

  const startMutation = useMutation({
    // A discriminated union, so an agent run can never be handed dimensions
    // and a tags run can never be handed free-text instructions.
    mutationFn: (
      input:
        | { mode: "agent"; scope: OrganizeScope; instructions?: string }
        | {
            mode: "tags";
            scope: OrganizeScope;
            dimensions: OrganizeDimension[];
            language: string;
          }
    ) => {
      if (Number.isNaN(numericProjectId)) throw new Error("no project selected");
      return input.mode === "tags"
        ? organizeService.requestTagOrganization(
            folderId,
            numericProjectId,
            input.scope,
            input.dimensions,
            input.language
          )
        : organizeService.requestOrganization(
            folderId,
            input.scope,
            input.instructions,
            numericProjectId
          );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requestKey(projectId, folderId) });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!request) throw new Error("no organize request to cancel");
      return organizeService.cancelRequest(request.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requestKey(projectId, folderId) });
      // The proposal no longer awaits review. OrganizeWatcher would refresh the
      // Folders-nav dot too, but only if its realtime event arrives — a dropped
      // one would leave the dot lit until the 60s poll, for a request this tab
      // just resolved itself.
      queryClient.invalidateQueries({ queryKey: [AWAITING_REVIEW_KEY] });
    },
  });

  const applyMutation = useMutation({
    mutationFn: ({
      excludedDocumentIds,
      folderNameOverrides,
      assignmentOverrides,
    }: {
      excludedDocumentIds: number[];
      folderNameOverrides?: Record<string, string>;
      assignmentOverrides?: Record<string, string>;
    }) => {
      if (!request) throw new Error("no organize proposal to apply");
      return organizeService.applyProposal(
        request.id,
        excludedDocumentIds,
        folderNameOverrides ?? {},
        assignmentOverrides ?? {}
      );
    },
    onSuccess: (result) => {
      // Request row changed status either way (applied/failed/expired), so the
      // proposal is off the awaiting-review count regardless of the outcome.
      queryClient.invalidateQueries({ queryKey: requestKey(projectId, folderId) });
      queryClient.invalidateQueries({ queryKey: [AWAITING_REVIEW_KEY] });
      if (result.status !== "applied") return;
      // New/changed folders and moved documents — same invalidations the
      // folder CRUD hook performs (useCustomFolders).
      queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId, projectId] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      onApplied?.();
    },
  });

  return {
    request,
    isLoading: requestQuery.isLoading,
    isError: requestQuery.isError,
    refetchRequest: requestQuery.refetch,
    start: startMutation.mutateAsync,
    isStarting: startMutation.isPending,
    cancel: cancelMutation.mutateAsync,
    isCancelling: cancelMutation.isPending,
    apply: applyMutation.mutateAsync,
    isApplying: applyMutation.isPending,
  };
}
